/*
  lb-range.js
  Loaded only by sections/lb-main-collection-range.liquid.

  The kit tabs and the leather row are ordinary links to Search & Discovery
  filter URLs, so they work without JavaScript. With JavaScript, a click goes
  through the theme's own filter engine (the <sht-coll-prd-fltr-frm> element
  from component-product-filter.js): the same section fetch, cache and history
  entry the Filter drawer uses. This file never modifies that engine. It only
  calls renderPage() and adds what the engine lacks:
    - keeping the tab and leather states in step after any filter change
    - redrawing the tab and leather rows from the engine's own cached
      response, because the engine only replaces the product grid and a
      drawer change can empty a tab (Harness kits under a Collar filter)
    - the browser back and forward buttons
*/
(() => {
  const ROOT = '[data-lb-range]';
  const GRID = '#mainCollectionProductContainer';

  const engine = () => {
    const el = document.querySelector('sht-coll-prd-fltr-frm');
    return el && typeof el.renderPage === 'function' ? el : null;
  };

  function paint(root, params) {
    root.querySelectorAll('[data-lb-range-tabs], [data-lb-range-leather]').forEach((group) => {
      const active = params.getAll(group.dataset.param);
      const isTabs = group.hasAttribute('data-lb-range-tabs');
      group.querySelectorAll('[data-lb-range-value]').forEach((link) => {
        const value = link.dataset.lbRangeValue;
        const on = value ? active.includes(value) : isTabs && active.length === 0;
        if (on) link.setAttribute('aria-current', 'true');
        else link.removeAttribute('aria-current');
      });
    });
  }

  function nextParams(root, group, value) {
    const params = new URLSearchParams(window.location.search);
    const param = group.dataset.param;
    const isTabs = group.hasAttribute('data-lb-range-tabs');
    const current = params.getAll(param);

    params.delete('page');
    params.delete('section_id');
    params.delete(param);

    // Leather: one at a time, and the chosen one again clears it.
    const clearing = !isTabs && current.length === 1 && current[0] === value;
    if (value && !clearing) params.append(param, value);

    // Harness kits have no collar, so collar choices would empty the grid.
    if (isTabs && value && value === root.dataset.tabsResetOn) {
      (root.dataset.tabsResetParams || '')
        .split(',')
        .map((p) => p.trim())
        .filter(Boolean)
        .forEach((p) => params.delete(p));
    }
    return params;
  }

  // Tabs on one swipeable line (layout "scroll"): bring the chosen tab to
  // the middle of the line. Does nothing for the kit tabs, which never scroll.
  function reveal(root, smooth) {
    const strip = root.querySelector('.lb-range-tabs--scroll');
    if (!strip || strip.scrollWidth <= strip.clientWidth) return;
    const tab = strip.querySelector('[aria-current="true"]');
    if (!tab) return;
    const s = strip.getBoundingClientRect();
    const t = tab.getBoundingClientRect();
    const left = strip.scrollLeft + (t.left - s.left) - (s.width - t.width) / 2;
    const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    strip.scrollTo({ left: Math.max(0, left), behavior: smooth && !still ? 'smooth' : 'auto' });
  }

  function setBusy(root, busy) {
    const grid = root.querySelector(GRID);
    if (!grid) return;
    clearTimeout(grid._lbRangeTimer);
    if (busy) {
      grid.setAttribute('aria-busy', 'true');
      grid._lbRangeTimer = setTimeout(() => grid.removeAttribute('aria-busy'), 8000);
    } else {
      grid.removeAttribute('aria-busy');
    }
  }

  // The engine caches every section response by URL. After it redraws the
  // grid, read that same response and copy over the tab and leather rows,
  // so a tab with no matches turns inert without a page reload. No extra
  // request is made; if the response is not in the cache, nothing changes.
  const SORT = (qs) => {
    const p = new URLSearchParams(qs);
    p.delete('section_id');
    return [...p.entries()].map(([k, v]) => `${k}=${v}`).sort().join('&');
  };

  function cachedSection(form) {
    const list = Array.isArray(form.cached_results) ? form.cached_results : [];
    if (!list.length) return null;
    const want = SORT(window.location.search);
    for (let i = list.length - 1; i >= 0; i -= 1) {
      const entry = list[i];
      if (!entry || typeof entry.url !== 'string') continue;
      const qs = entry.url.split('?')[1] || '';
      if (SORT(qs) === want) return entry.html;
    }
    return null;
  }

  function syncRows(root) {
    const form = engine();
    const html = form && cachedSection(form);
    if (!html) return;
    const fresh = new DOMParser().parseFromString(html, 'text/html');
    ['[data-lb-range-tabs]', '[data-lb-range-leather]'].forEach((sel) => {
      const now = root.querySelector(sel);
      const next = fresh.querySelector(sel);
      if (!now || !next || now.outerHTML === next.outerHTML) return;
      // Keep keyboard focus on the same choice after the swap.
      const focused = now.contains(document.activeElement) ? document.activeElement : null;
      const key = focused && (focused.dataset.lbRangeValue ?? focused.textContent.trim());
      const node = document.importNode(next, true);
      now.replaceWith(node);
      if (key != null) {
        const again = [...node.querySelectorAll('a')].find(
          (a) => (a.dataset.lbRangeValue ?? a.textContent.trim()) === key
        );
        if (again) again.focus({ preventScroll: true });
      }
    });
  }

  function init(root) {
    if (!root || root.dataset.lbRangeReady === 'true') return;
    root.dataset.lbRangeReady = 'true';

    root.addEventListener('click', (event) => {
      const link = event.target.closest('[data-lb-range-tabs] a, [data-lb-range-leather] a');
      if (!link || event.defaultPrevented || event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const form = engine();
      if (!form) return; // no filter engine on this page: follow the link normally

      event.preventDefault();
      const params = nextParams(root, link.closest('[data-param]'), link.dataset.lbRangeValue);
      paint(root, params);
      reveal(root, true);
      setBusy(root, true);
      form.renderPage(params.toString());
    });

    const grid = root.querySelector(GRID);
    if (grid) {
      // The engine replaces the grid's contents after every filter change,
      // including changes made in the drawer or with the active-filter tags.
      new MutationObserver(() => {
        setBusy(root, false);
        // The engine adds the response to its cache just after drawing the
        // grid, so wait one task before reading it.
        setTimeout(() => {
          syncRows(root);
          paint(root, new URLSearchParams(window.location.search));
          reveal(root, false);
        }, 0);
      }).observe(grid, { childList: true });
    }
    reveal(root, false);
  }

  const initAll = () => document.querySelectorAll(ROOT).forEach(init);

  window.addEventListener('popstate', () => {
    const root = document.querySelector(ROOT);
    const form = engine();
    if (!root || !form) return;
    // renderPage() normally adds a history entry; on back/forward, skip exactly one.
    const push = form.updateURL;
    form.updateURL = function () {
      form.updateURL = push;
    };
    paint(root, new URLSearchParams(window.location.search));
    setBusy(root, true);
    form.renderPage(window.location.search.replace(/^\?/, ''));
  });

  document.addEventListener('shopify:section:load', initAll);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
})();
